import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Building } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';

interface School {
  school_id: string;
  school_name: string;
  district_code: string;
  district_name: string;
  school_level: string;
  grade_range: string;
  focus_category: string;
  is_active: boolean;
  total_capacity: number;
  current_enrollment: number;
  utilization_percentage: number;
}

interface AdminSchoolContextType {
  // Current state
  currentSchoolView: School | null;
  isSystemView: boolean;
  isLoading: boolean;
  isSwitching: boolean;
  switchingTo: string | null; // School name being switched to

  // Available schools
  availableSchools: School[];
  groupedSchools: Record<string, School[]>;

  // Actions
  switchToSchool: (schoolId: string) => Promise<void>;
  switchToSystemView: () => void;
  refreshSchools: () => Promise<void>;

  // Permissions
  canSwitchSchools: boolean;
  accessLevel: 'none' | 'school_staff' | 'district_admin' | 'system_admin';

  // Navigation helpers
  getSchoolUrl: (schoolId: string, tab?: string, subtab?: string) => string;
  getCurrentContext: () => string;
}

const AdminSchoolContext = createContext<AdminSchoolContextType | undefined>(undefined);

interface AdminSchoolProviderProps {
  children: React.ReactNode;
}

export const AdminSchoolProvider: React.FC<AdminSchoolProviderProps> = ({ children }) => {
  const { user, hasRole } = useAuth();
  const [currentSchoolView, setCurrentSchoolView] = useState<School | null>(null);
  const [isSystemView, setIsSystemView] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const [availableSchools, setAvailableSchools] = useState<School[]>([]);
  const [groupedSchools, setGroupedSchools] = useState<Record<string, School[]>>({});

  // Determine access level
  const accessLevel = hasRole('admin') ? 'system_admin' :
                     hasRole('district_admin') ? 'district_admin' :
                     hasRole('school_staff') ? 'school_staff' : 'none';
  
  const canSwitchSchools = accessLevel !== 'none';

  // Fetch available schools based on user permissions
  const refreshSchools = useCallback(async () => {
    if (!user || !canSwitchSchools) return;
    
    setIsLoading(true);
    try {
      let { data, error } = await supabase
        .rpc('get_admin_accessible_schools', { p_user_id: user.id });

      if (error?.code === '42501') {
        // get_admin_accessible_schools grants EXECUTE to `authenticated` and never
        // raises 42501 for a role-less caller (it returns empty rows instead), so a
        // permission-denied here means the request went out under a stale session
        // that had fallen back to `anon` -- the same class of bug as the realtime
        // JWT-rotation issue already tracked as Sentry MAGNETFLOW-PORTAL-4. Refresh
        // once and retry before giving up.
        await supabase.auth.refreshSession();
        ({ data, error } = await supabase
          .rpc('get_admin_accessible_schools', { p_user_id: user.id }));
      }

      if (error) throw error;

      const schools = data || [];
      setAvailableSchools(schools);
      
      // Group schools by district for better UX
      const grouped = schools.reduce((acc: Record<string, School[]>, school) => {
        const district = school.district_name || 'Unknown District';
        if (!acc[district]) acc[district] = [];
        acc[district].push(school);
        return acc;
      }, {});

      // Render districts alphabetically; pin "Unknown District" to the end.
      // Object key iteration order follows insertion order, so rebuild sorted.
      const sortedGrouped: Record<string, School[]> = {};
      Object.keys(grouped)
        .sort((a, b) => {
          if (a === 'Unknown District') return 1;
          if (b === 'Unknown District') return -1;
          return a.localeCompare(b);
        })
        .forEach(district => {
          sortedGrouped[district] = grouped[district];
        });

      setGroupedSchools(sortedGrouped);
    } catch (error) {
      console.error('Error fetching admin accessible schools:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AdminSchool', { context: 'fetch accessible schools' });
      toast.error('Failed to load available schools');
    } finally {
      setIsLoading(false);
    }
  }, [user, canSwitchSchools]);

  // Load schools on mount and when auth changes
  useEffect(() => {
    refreshSchools();
  }, [refreshSchools]);

  // Initialize from URL parameters or localStorage
  useEffect(() => {
    if (!canSwitchSchools || availableSchools.length === 0) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const schoolViewParam = urlParams.get('admin_school_view');
    
    // Check localStorage for last viewed school
    const lastViewedSchool = localStorage.getItem('admin_last_school_view');
    
    if (schoolViewParam && schoolViewParam !== 'system') {
      const school = availableSchools.find(s => s.school_id === schoolViewParam);
      if (school) {
        setCurrentSchoolView(school);
        setIsSystemView(false);
        localStorage.setItem('admin_last_school_view', schoolViewParam);
        return;
      }
    }
    
    if (lastViewedSchool && lastViewedSchool !== 'system') {
      const school = availableSchools.find(s => s.school_id === lastViewedSchool);
      if (school) {
        setCurrentSchoolView(school);
        setIsSystemView(false);
        // Don't auto-update URL on initial load
        return;
      }
    }
    
    // Default to system view
    setIsSystemView(true);
    setCurrentSchoolView(null);
  }, [availableSchools, canSwitchSchools]);

  // Switch to a specific school
  const switchToSchool = useCallback(async (schoolId: string) => {
    if (!user || !canSwitchSchools) return;

    const school = availableSchools.find(s => s.school_id === schoolId);
    if (!school) {
      toast.error('School not found or not accessible');
      return;
    }

    // Set switching state
    setIsSwitching(true);
    setSwitchingTo(school.school_name);

    // Validate access
    try {
      const { data: validation, error } = await supabase
        .rpc('validate_admin_school_access', {
          p_user_id: user.id,
          p_school_id: schoolId
        });

      if (error || !validation?.can_access) {
        toast.error('Access denied to this school');
        setIsSwitching(false);
        setSwitchingTo(null);
        return;
      }

      // Log the switch (non-blocking - don't fail if logging fails)
      try {
        await supabase.rpc('log_admin_school_switch', {
          p_user_id: user.id,
          p_from_context: getCurrentContext(),
          p_to_school_id: schoolId,
          p_to_context: `school_dashboard:${school.school_name}`,
          p_session_id: window.location.pathname
        });
      } catch (logError) {
        console.warn('Failed to log school switch:', logError);
        EnhancedErrorLogger.getInstance().logQuickError(logError, 'AdminSchool', { context: 'log school switch' });
      }

      // Update URL and localStorage first
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.set('admin_school_view', schoolId);
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      window.history.pushState({}, '', newUrl);
      localStorage.setItem('admin_last_school_view', schoolId);

      // Dispatch event to notify components to prepare for switch
      window.dispatchEvent(new CustomEvent('admin-school-switching', {
        detail: { schoolId, schoolName: school.school_name }
      }));

      // Update state - this triggers re-render of school dashboard
      setCurrentSchoolView(school);
      setIsSystemView(false);

      // Dispatch event to notify components data context changed
      window.dispatchEvent(new CustomEvent('admin-school-switched', {
        detail: { schoolId, schoolName: school.school_name }
      }));

      // Keep loading state for a moment to allow data to start loading
      // Components should call markSwitchComplete when their data is ready
      await new Promise(resolve => setTimeout(resolve, 800));

      toast.success(`Switched to ${school.school_name}`);

    } catch (error) {
      console.error('Error switching to school:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AdminSchool', { context: 'switch school' });
      toast.error('Failed to switch to school');
    } finally {
      setIsSwitching(false);
      setSwitchingTo(null);
    }
  }, [user, canSwitchSchools, availableSchools]);

  // Switch to system view
  const switchToSystemView = useCallback(async () => {
    if (!user) return;

    setIsSwitching(true);
    setSwitchingTo('Admin Dashboard');

    // Log the switch back to system view (non-blocking)
    try {
      await supabase.rpc('log_admin_school_switch', {
        p_user_id: user.id,
        p_from_context: getCurrentContext(),
        p_to_school_id: null,
        p_to_context: 'admin_system_overview',
        p_session_id: window.location.pathname
      });
    } catch (error) {
      console.warn('Failed to log system view switch:', error);
      EnhancedErrorLogger.getInstance().logQuickError(error, 'AdminSchool', { context: 'log system view switch' });
    }

    // Dispatch event to notify components
    window.dispatchEvent(new CustomEvent('admin-school-switching', {
      detail: { schoolId: null, schoolName: 'Admin Dashboard' }
    }));

    // Update URL and localStorage
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('admin_school_view', 'system');
    const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    localStorage.setItem('admin_last_school_view', 'system');

    setCurrentSchoolView(null);
    setIsSystemView(true);

    // Dispatch completion event
    window.dispatchEvent(new CustomEvent('admin-school-switched', {
      detail: { schoolId: null, schoolName: 'Admin Dashboard' }
    }));

    // Keep loading state briefly
    await new Promise(resolve => setTimeout(resolve, 600));

    toast.success('Returned to Admin Dashboard');

    setIsSwitching(false);
    setSwitchingTo(null);
  }, [user]);

  // Helper to get current context description
  const getCurrentContext = useCallback(() => {
    if (isSystemView) return 'admin_system_overview';
    if (currentSchoolView) return `school_dashboard:${currentSchoolView.school_name}`;
    return 'unknown';
  }, [isSystemView, currentSchoolView]);

  // Helper to generate school URLs
  const getSchoolUrl = useCallback((schoolId: string, tab?: string, subtab?: string) => {
    const params = new URLSearchParams();
    params.set('admin_school_view', schoolId);
    if (tab) params.set('tab', tab);
    if (subtab) params.set('subtab', subtab);
    return `${window.location.pathname}?${params.toString()}`;
  }, []);

  const value: AdminSchoolContextType = {
    currentSchoolView,
    isSystemView,
    isLoading,
    isSwitching,
    switchingTo,
    availableSchools,
    groupedSchools,
    switchToSchool,
    switchToSystemView,
    refreshSchools,
    canSwitchSchools,
    accessLevel,
    getSchoolUrl,
    getCurrentContext
  };

  return (
    <AdminSchoolContext.Provider value={value}>
      {children}

      {/* Full-page loading overlay during school switch */}
      <AnimatePresence>
        {isSwitching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-white/80 backdrop-blur-sm z-[100] flex items-center justify-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="bg-white rounded-xl shadow-xl border border-gray-200 px-8 py-6 flex flex-col items-center"
            >
              <motion.div
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1, type: "spring", stiffness: 200 }}
                className="w-12 h-12 rounded-full bg-gradient-to-br from-nwsisd-teal to-nwsisd-dark-blue flex items-center justify-center mb-4"
              >
                <Building className="h-6 w-6 text-white" />
              </motion.div>
              <Loader2 className="h-6 w-6 text-nwsisd-teal animate-spin mb-3" />
              <p className="text-sm text-gray-500 mb-1">Switching to</p>
              <motion.p
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: 0.15 }}
                className="text-lg font-semibold text-gray-900"
              >
                {switchingTo}
              </motion.p>
              <p className="text-xs text-gray-400 mt-2">Loading dashboard data...</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminSchoolContext.Provider>
  );
};

// Custom hook to use the AdminSchoolContext
export const useAdminSchoolContext = (): AdminSchoolContextType => {
  const context = useContext(AdminSchoolContext);
  if (context === undefined) {
    throw new Error('useAdminSchoolContext must be used within an AdminSchoolProvider');
  }
  return context;
};

// Helper hook for non-admin users (returns null)
export const useAdminSchoolContextOptional = (): AdminSchoolContextType | null => {
  try {
    return useAdminSchoolContext();
  } catch {
    return null;
  }
};

// Page transition wrapper for smooth view changes
interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
}

export const AdminPageTransition: React.FC<PageTransitionProps> = ({ children, className }) => {
  const { currentSchoolView, isSystemView } = useAdminSchoolContext();

  // Create a key that changes when the view changes
  const viewKey = isSystemView ? 'system' : currentSchoolView?.school_id || 'loading';

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={viewKey}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};